import React, { useState, useEffect, useCallback } from 'react';
import { documentsApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileArrowUp, FilePdf, Trash, DownloadSimple, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await documentsApi.list();
      setDocs(res.data);
    } catch { toast.error('Error al cargar documentos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async () => {
    if (!name.trim() || !file) {
      toast.error('Ingresa un nombre y selecciona un archivo PDF'); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('file', file);
      await documentsApi.upload(formData);
      toast.success('Documento subido exitosamente');
      setUploadOpen(false);
      setName('');
      setFile(null);
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir documento');
    } finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await documentsApi.delete(deleting);
      toast.success('Documento eliminado');
      setDeleting(null);
      fetchDocs();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleDownload = (doc) => {
    const token = localStorage.getItem('token');
    const url = documentsApi.download(doc.id);
    const a = document.createElement('a');
    a.href = `${url}?token=${token}`;
    a.target = '_blank';
    a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Documentos</h1>
          <p className="text-[#2B4B8C]/60 text-sm">Documentos PDF visibles para todos los contratistas</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="upload-btn">
          <FileArrowUp size={16} className="mr-1.5" /> Subir Documento
        </Button>
      </div>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          {docs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#2B4B8C]/5">
                  <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Archivo</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Peso</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Fecha</TableHead>
                  <TableHead className="text-right text-[#2B4B8C]/60">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map(d => (
                  <TableRow key={d.id} className="border-[#2B4B8C]/5" data-testid={`doc-row-${d.id}`}>
                    <TableCell className="font-medium text-[#2B4B8C]">
                      <div className="flex items-center gap-2">
                        <FilePdf size={18} className="text-red-500 shrink-0" />
                        {d.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#2B4B8C]/60 text-sm">{d.original_filename}</TableCell>
                    <TableCell className="text-[#2B4B8C]/60 text-sm">{formatSize(d.size)}</TableCell>
                    <TableCell className="text-[#2B4B8C]/60 text-sm">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString('es-CL') : ''}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(d)} className="text-[#2B4B8C]/60 hover:text-[#2BBAA4]" data-testid={`download-${d.id}`}>
                          <DownloadSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(d.id)} className="text-red-400 hover:text-red-500" data-testid={`delete-doc-${d.id}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16 text-[#2B4B8C]/40">
              <FilePdf size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No hay documentos subidos</p>
              <p className="text-xs mt-1">Sube documentos PDF para que los contratistas puedan verlos</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">Subir Documento</DialogTitle>
            <DialogDescription className="text-[#2B4B8C]/60">El documento sera visible para todos los contratistas</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-medium text-[#2B4B8C]/60 mb-1 block">Nombre del documento</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Reglamento interno 2026"
                className="border-[#2B4B8C]/15"
                data-testid="doc-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#2B4B8C]/60 mb-1 block">Archivo PDF</label>
              <div className="border border-dashed border-[#2B4B8C]/20 rounded-lg p-4 text-center">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FilePdf size={20} className="text-red-500" />
                    <span className="text-sm text-[#2B4B8C]">{file.name}</span>
                    <button onClick={() => setFile(null)} className="text-[#2B4B8C]/30 hover:text-red-500 ml-2">&times;</button>
                  </div>
                ) : (
                  <>
                    <FileArrowUp size={24} className="mx-auto mb-1 text-[#2B4B8C]/30" />
                    <p className="text-xs text-[#2B4B8C]/40 mb-2">Maximo 20 MB</p>
                  </>
                )}
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className={file ? 'hidden' : 'text-sm text-[#2B4B8C]/60 file:mr-2 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:bg-[#2B4B8C]/10 file:text-[#2B4B8C] cursor-pointer'}
                  data-testid="doc-file-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setUploadOpen(false); setName(''); setFile(null); }} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
              <Button onClick={handleUpload} disabled={uploading || !name.trim() || !file} className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="confirm-upload-btn">
                <FileArrowUp size={14} className="mr-1.5" /> {uploading ? 'Subiendo...' : 'Subir'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Warning size={20} className="text-red-500" /> Eliminar documento
            </DialogTitle>
            <DialogDescription className="text-[#2B4B8C]/60 pt-1">
              El documento dejara de estar disponible para los contratistas. Esta accion no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setDeleting(null)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={handleDelete} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-doc-btn">
              <Trash size={14} className="mr-1.5" /> Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
